import React, { forwardRef, useEffect, useRef, useState } from "react"
import VariantReader from "aldoo-ra/VariantReader"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { XMarkIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import { twMerge } from "tailwind-merge"

const Input = forwardRef(
  (
    {
      label,
      value,
      defaultValue,
      onChange,
      placeholder = "",
      type = "text",
      error = "",
      name = "",
      className = "",
      labelClassName = "",
      required = false,
      multiline = false,
      onKeyDown,
      onEnter,
      onBlur,
      onClear,
      readOnly = false,
      icon = null,
      variant = "default",
      styleOverrides = {},
      showClearButton = false,
      clearButtonVariant = "default",
      clearButtonClassName = "",
      autoFocus = false,
      min,
      max,
      rows = 1,
      localUserPreferencesKey = null, // Key for storing user preferences
      debounce = 0, // Debounce delay in milliseconds, 0 to disable
      showLoading = false, // Show loading spinner with "Loading..." text
    },
    ref
  ) => {
    // Create internal ref if no ref is provided
    const internalRef = useRef(null)

    // Use the passed ref or fall back to internal ref
    const inputRef = ref || internalRef
    
    // Determine if this is a controlled or uncontrolled component
    const isControlled = value !== undefined

    // Debounce state and ref for timeout
    const [debouncedValue, setDebouncedValue] = useState(value ?? defaultValue ?? "")
    const debounceTimeoutRef = useRef(null)

    // Update debounced value when value prop changes (external updates)
    useEffect(() => {
      if (isControlled) {
        setDebouncedValue(value ?? "")
      }
    }, [value, isControlled])

    // Apply focus with visibility check and delay
    useEffect(() => {
      if (autoFocus) {
        const timeoutId = setTimeout(() => {
          if (inputRef.current) {
            // Check if element is visible
            const isVisible = (element) => {
              if (!element) return false

              // Get element properties
              const style = window.getComputedStyle(element)
              const rect = element.getBoundingClientRect()

              // Check for visibility conditions
              return (
                style.display !== "none" &&
                style.visibility !== "hidden" &&
                style.opacity !== "0" &&
                rect.width > 0 &&
                rect.height > 0 &&
                element.offsetParent !== null
              )
            }

            if (isVisible(inputRef.current)) {
              // Element is visible, safe to focus
              inputRef.current.focus()
              inputRef.current.select()

              // Select text if there's a value
              const currentValue = isControlled ? value : defaultValue
              if (currentValue && currentValue.length > 0) {
                inputRef.current.select()
              }
            }
          }
        }, 100) // Small delay to ensure DOM and visibility are settled

        return () => clearTimeout(timeoutId)
      }
    }, [autoFocus]) // Run only once on mount

    const variants = VariantReader.useVariant({
      componentName: "Input",
      variant,
      styleOverrides,
    })

    const handleClear = () => {
      if (onChange) {
        // Save to local user preferences if key is provided and input is controlled
        if (localUserPreferencesKey && localUserPreferencesKey.trim() && isControlled) {
          try {
            LocalUserPreferences.set(localUserPreferencesKey, "")
          } catch (error) {
            console.warn("Failed to save input preference:", error)
          }
        }
        
        // Create a synthetic event to mimic input change
        const event = {
          target: { value: "", name },
          preventDefault: () => {},
        }
        onChange(event)
      }
      onClear && onClear()
    }

    // Handle input changes with debouncing
    const handleInputChange = (e) => {
      const inputValue = e.target.value
      
      // Update local state immediately for responsive UI
      setDebouncedValue(inputValue)
      
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      
      // Handle debouncing
      if (debounce > 0 && onChange) {
        debounceTimeoutRef.current = setTimeout(() => {
          handleActualChange(e, inputValue)
        }, debounce)
      } else {
        // No debounce, call immediately
        handleActualChange(e, inputValue)
      }
    }

    // Handle the actual onChange logic
    const handleActualChange = (e, inputValue) => {
      // Save to local user preferences if key is provided and input is controlled
      if (localUserPreferencesKey && localUserPreferencesKey.trim() && isControlled) {
        try {
          LocalUserPreferences.set(localUserPreferencesKey, inputValue)
        } catch (error) {
          console.warn("Failed to save input preference:", error)
        }
      }

      // Create a synthetic event with the correct value to avoid stale event issues
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: inputValue,
          name: e.target.name,
          files: e.target.files // Preserve files for file inputs
        }
      }

      // For number inputs, allow empty string during typing
      if (type === "number") {
        if (inputValue === "") {
          // Allow empty string during typing
          onChange(syntheticEvent)
        } else {
          const numValue = parseFloat(inputValue)
          if (!isNaN(numValue)) {
            // Only update if it's a valid number
            onChange(syntheticEvent)
          }
          // If it's not a valid number, don't call onChange (ignore the input)
        }
      } else {
        // For non-number inputs, use synthetic event with correct value
        onChange(syntheticEvent)
      }
    }

    // Clean up timeout on unmount
    useEffect(() => {
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current)
        }
      }
    }, [])

    // Handle blur for number inputs
    const handleNumberBlur = (e) => {
      if (type === "number") {
        const inputValue = e.target.value
        
        if (inputValue === "" || inputValue === null || inputValue === undefined) {
          // Apply fallback logic when field is left empty
          const fallbackValue = min !== undefined ? min : 1
          
          // Create synthetic event with fallback value
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: fallbackValue.toString()
            }
          }
          
          // Call onChange with fallback value
          if (onChange) {
            onChange(syntheticEvent)
          }
        } else {
          const numValue = parseFloat(inputValue)
          
          // Check min/max constraints
          if (min !== undefined && numValue < min) {
            const syntheticEvent = {
              ...e,
              target: {
                ...e.target,
                value: min.toString()
              }
            }
            if (onChange) {
              onChange(syntheticEvent)
            }
          } else if (max !== undefined && numValue > max) {
            const syntheticEvent = {
              ...e,
              target: {
                ...e.target,
                value: max.toString()
              }
            }
            if (onChange) {
              onChange(syntheticEvent)
            }
          }
        }
      }
      
      // Call original onBlur handler
      if (onBlur) {
        onBlur(e)
      }
    }
    
    // Compose Enter handler with any caller-supplied onKeyDown. Plain Enter
    // fires onEnter; Shift+Enter / IME composition is ignored. For multiline
    // (textarea) inputs Enter is still treated as newline unless the caller
    // explicitly opts in by passing onEnter.
    const handleKeyDown = (e) => {
      if (onKeyDown) onKeyDown(e)
      if (e.defaultPrevented) return
      if (onEnter && e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
        if (multiline) return
        onEnter(e)
      }
    }

    const inputProps = {
      onBlur: handleNumberBlur,
      onKeyDown: handleKeyDown,
      ...(isControlled ? { value: debounce > 0 ? debouncedValue : value } : { defaultValue }),
      onChange: onChange ? handleInputChange : undefined,
      placeholder,
      name,
      readOnly,
      ref: inputRef,
      min,
      max,
      // Include React's built-in autoFocus attribute as well
      autoFocus,
      className: twMerge(
        variants.input,
        icon ? variants.input_with_icon : "",
        error ? variants.error_state : "",
        showClearButton ? "pr-8" : "",
        className
      ),
    }

    return (
      <div className={twMerge(variants.root, className)}>
        {label && (
          <label className={twMerge(variants.label, labelClassName)}>
            {label} {required && <span className={variants.required}>*</span>}
          </label>
        )}
        <div className={twMerge(variants.input_wrapper, "relative")}>
          {icon && <div className={variants.icon_wrapper}>{icon}</div>}
          {multiline ? (
            <textarea
              {...inputProps}
              rows={rows}
              className={twMerge(
                variants.textarea,
                icon ? variants.input_with_icon : "",
                showLoading ? "pl-8" : "", // Add left padding for spinner
                error ? variants.error_state : "",
                className
              )}
              disabled={showLoading}
              readOnly={showLoading || inputProps.readOnly}
            />
          ) : (
            <input 
              type={type} 
              {...inputProps} 
              className={twMerge(
                inputProps.className,
                showLoading ? "pl-8" : "" // Add left padding for spinner
              )}
              disabled={showLoading}
              readOnly={showLoading || inputProps.readOnly}
            />
          )}
          
          {/* Loading spinner inside input */}
          {showLoading && (
            <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
              <Spinner variant="minimal" />
            </div>
          )}
          
          {showClearButton && (isControlled ? (debounce > 0 ? debouncedValue : value) : inputRef.current?.value) && !readOnly && !showLoading && (
            <Button
              variant={clearButtonVariant}
              type="button"
              onClick={handleClear}
              className={twMerge(
                "absolute right-4 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text",
                clearButtonClassName
              )}
              aria-label="Clear input"
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
        {error && <p className={variants.error_text}>{error}</p>}
      </div>
    )
  }
)

Input.displayName = "Input"
export default Input
